import { TokenRefreshLink } from "apollo-link-token-refresh";
import { setContext } from "@apollo/client/link/context";
import Cookies from "js-cookie";
import { onError } from "@apollo/client/link/error";
import { CachePersistor } from "apollo-cache-persist";
import merge from "lodash.merge";
import { ApolloLink, Observable, ApolloClient, createHttpLink } from "@apollo/client";

import { getLocalStorageToken, executeRefreshTokenRequest, isLoggedIn, forceLogOut } from "../helpers/auth";
import { getUtmParameters } from "../helpers/Analytics";
import {
  CAPACITOR_APP_VERSION,
  ERROR_CODE_UNAUTHORIZED,
  ERROR_MESSAGE_DATA_NOT_OWNED_BY_MEMBER,
  FETCH_POLICY_NETWORK_ONLY,
} from "../helpers/constants";
import { isCapacitorNative } from "../helpers/isCapacitorNative";
import { logError } from "../helpers/logging";
import { getWithExpiry, setWithExpiry } from "../helpers/storage";
import { getGraphQlURL } from "../helpers/urls.helper";
import { writeMissingCacheDefaults } from "./cacheDefaults";
import {
  GET_AUTH,
  GET_GIFT_FLOW_INFO,
  GET_REFERRAL_DISCOUNT,
  GET_GUEST_FREE_BOX_CAMPAIGN_DISCOUNT,
  GET_GUEST_PROMO_CODE_DISCOUNT,
  GET_LOCAL_SHOPPING_CART,
} from "./queries";
import cache from "./cache";

// Apollo GraphQL Resolvers
import { resolverAuth } from "./resolvers/auth";
import {
  migratePersistedLocalCart,
  refreshLocalCartOrderMinimumFlags,
  resolverLocalCart,
} from "./resolvers/cart";
import { resolverGiftFlow } from "./resolvers/gift";
import { resolverReferralDiscount } from "./resolvers/member";
import { resolverGuestFreeBoxCampaignDiscount } from "./resolvers/order";

const defaultOptions = {
  watchQuery: {
    fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: "all",
  },
  mutate: {
    errorPolicy: "all",
  },
};

// Cooldown window after a post-refresh-token reload. If the backend keeps
// returning UNAUTHORIZED (e.g. an endpoint that shouldn't require auth but
// does), this stops the refresh → reload → refresh cycle from saturating the
// network.
const AUTH_RELOAD_COOLDOWN_KEY = "auth_reload_triggered";
const AUTH_RELOAD_COOLDOWN_MS = 10_000;

// Cap the persisted Apollo cache. It was previously `false` (unlimited) since
// the original 2019 setup; on a query-heavy `network-only` checkout the cache
// grows toward Safari's ~5MB localStorage origin quota (shared with the auth
// token) and throws QuotaExceededError. Cap well under that — when exceeded the
// persistor purges and pauses persistence rather than throwing.
const PERSISTED_CACHE_MAX_BYTES = 2_500_000;

/**
 * Creates instance of Apollo client and Persistence and make it available across the application.
 * @return {{client: ApolloClient<NormalizedCacheObject> ,persistent: CachePersistor<NormalizedCacheObject>}}
 * */
const generateApolloClient = () => {
  const graphqlURL = getGraphQlURL();
  const httpLink = createHttpLink({
    uri: graphqlURL,
    credentials: "include",
    // No explicit fetch: Apollo uses the browser's native fetch. IE/old
    // browsers without fetch are gated out by isUnSupportedBrowser() before
    // the client is reached, so the unfetch polyfill isn't needed here.
  });

  // Returns headers to the context so httpLink can read them
  const authLink = setContext((_, { headers }) => {
    // Gets the authentication token from local storage if it exists
    const token = getLocalStorageToken().accessToken;

    // _fbp and _fbc helps identify the current user by facebook.
    // It's sent to the backend to help event deduplication
    // and improve matching
    const fbp = Cookies.get("_fbp") ? Cookies.get("_fbp") : "";
    const fbc = Cookies.get("_fbc") ? Cookies.get("_fbc") : "";

    // Forwards UTM parameters to base
    const utmParameters = getUtmParameters();
    // HTTP headers with underscore are stripped by the server so we need to change them to dashes
    // e.g. utm_campaign -> utm-campaign
    Object.keys(utmParameters).forEach((key) => {
      const newKey = key.replace("_", "-");
      utmParameters[newKey] = utmParameters[key];
      delete utmParameters[key];
    });

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        fbp,
        fbc,
        ...utmParameters,
      },
    };
  });
  const refreshTokenLink = new TokenRefreshLink({
    accessTokenField: "access_token",

    // Refreshes token only for Members who already have a token which is expired
    isTokenValidOrUndefined: () => !getLocalStorageToken().accessToken || isLoggedIn(true),
    fetchAccessToken: () => executeRefreshTokenRequest(),
    handleError: (err) => {
      logError(err);
      forceLogOut();
    },
  });

  let isAccessValidationBeingExecuted = false;

  // apollo-link that is responsible for refreshing a token or logging user out in case their access token
  // is not valid. It might happen when client thinks user is authenticated, but API returns unauthorized
  // error.
  // eslint-disable-next-line consistent-return
  const validateAuthorizationLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      const hasUnauthorizedError = graphQLErrors.some((err) => err.code === ERROR_CODE_UNAUTHORIZED);
      const isTryingToAccessOthersData = graphQLErrors.some(
        (err) => err.message === ERROR_MESSAGE_DATA_NOT_OWNED_BY_MEMBER
      );

      // In case of multiple operations that fail with unauthorised error we refresh a token only once
      if (isAccessValidationBeingExecuted && hasUnauthorizedError && isLoggedIn()) {
        // Does nothing for other unauthorised operations that need to wait for fresh token as the page
        // will be reloaded after successful refresh
        return new Observable(() => {});
      }

      // If the authorized error is detected
      if (!isAccessValidationBeingExecuted && hasUnauthorizedError && isLoggedIn()) {
        isAccessValidationBeingExecuted = true;

        // If multiple memberIds detected in local cache, clear the cache and force log the user out
        if (isTryingToAccessOthersData) {
          return new Observable(() => {
            // Member trying to access data of other member
            isAccessValidationBeingExecuted = false;
            return forceLogOut();
          });
        }

        // If it is a token issue, refreshes token through async request
        return new Observable((observer) => {
          executeRefreshTokenRequest()
            .then((refreshResponse) => {
              const accessToken = refreshResponse.access_token;

              // Updates headers with a new access token
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: accessToken ? `Bearer ${accessToken}` : "",
                },
              }));
              isAccessValidationBeingExecuted = false;
            })
            .then(() => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };

              // Retries query/mutation that failed with unauthorised error
              forward(operation).subscribe(subscriber);

              isAccessValidationBeingExecuted = false;

              // Reloads the page to refetch all unauthorised queries/mutations
              // instead of retrying them one by one.
              if (!getWithExpiry(AUTH_RELOAD_COOLDOWN_KEY)) {
                setWithExpiry(AUTH_RELOAD_COOLDOWN_KEY, true, AUTH_RELOAD_COOLDOWN_MS);
                window.location.reload();
              }
            })
            .catch((error) => {
              // If token could not be refreshed user is logged out
              observer.error(error);
              isAccessValidationBeingExecuted = false;
              return forceLogOut();
            });
        });
      }
    }

    if (networkError) {
      const operationName = operation.operationName ?? "GraphQL operation";

      if (networkError.statusCode === 400) {
        const errors = networkError.result && networkError.result.errors;
        if (errors && errors.length) {
          logError(`${operationName}: ${errors[0].message}`);
        }
      } else {
        // Network errors without a 4xx response (aborted requests, in-app
        // browser sandbox failures, CORS issues, flaky connections) carry very
        // little by default — on WebKit they surface as a bare
        // `TypeError: Load failed`. Attach context so each occurrence is
        // triageable: which operation, where it was going, connectivity at the
        // time, and the error's own fields.
        const { connection } = navigator;
        const errorToLog =
          networkError instanceof Error
            ? networkError
            : new Error(`${operationName}: ${networkError?.message ?? "network request failed"}`);

        logError(errorToLog, {
          operationName,
          url: getGraphQlURL(),
          statusCode: networkError?.statusCode,
          errorName: networkError?.name,
          errorMessage: networkError?.message,
          // Was the device even online when it failed? Distinguishes genuine
          // failures from users navigating away / losing signal mid-request.
          online: navigator.onLine,
          connectionType: connection?.effectiveType,
        });
      }
    }
  });

  const link = ApolloLink.from([refreshTokenLink, validateAuthorizationLink, authLink, httpLink]);

  // localStorage adapter that surfaces quota failures. The maxSize cap below
  // prevents the persisted cache from growing unbounded, but if the origin's
  // storage still fills (shared with the auth token etc.), a silent
  // QuotaExceededError would otherwise stop persistence with no signal. Report
  // it and drop the persisted cache to free space so the app keeps working.
  const persistedCacheKey = import.meta.env.REACT_APP_STORE_LOCAL_STORAGE;
  const quotaAwareStorage = {
    getItem: (key) => window.localStorage.getItem(key),
    removeItem: (key) => window.localStorage.removeItem(key),
    setItem: (key, value) => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        logError(error, { location: "CachePersistor.setItem", key, valueLength: value?.length });
        try {
          window.localStorage.removeItem(persistedCacheKey);
        } catch {
          // Nothing more we can do; persistence is best-effort.
        }
      }
    },
  };

  const persistent = new CachePersistor({
    cache,
    storage: quotaAwareStorage,
    maxSize: PERSISTED_CACHE_MAX_BYTES,
    debug: import.meta.env.REACT_APP_DEBUG === "true", // enables console logging
    key: persistedCacheKey,
    debounce: 5, // ms of delay before changes are written to the local storage
  });

  const resolvers = {
    ...merge(
      resolverAuth.resolvers,
      resolverGiftFlow.resolvers,
      resolverReferralDiscount.resolvers,
      resolverGuestFreeBoxCampaignDiscount.resolvers,
      resolverLocalCart.resolvers
    ),
  };

  const typeDefs = {
    ...merge(
      resolverAuth.typeDefs,
      resolverGiftFlow.typeDefs,
      resolverReferralDiscount.typeDefs,
      resolverGuestFreeBoxCampaignDiscount.typeDefs,
      resolverLocalCart.typeDefs
    ),
  };

  // Instantiates Apollo Client object for GraphQL
  const client = new ApolloClient({
    defaultOptions,
    cache,
    link,
    typeDefs,
    resolvers,
    /* On Capacitor, advertise the app version so /graphql-mobile's client
     * gate accepts mutations. Apollo emits this as the
     * `apollographql-client-version` HTTP header on every operation;
     * without it the backend returns UNSUPPORTED_CLIENT_VERSION. Matches
     * the RN app, which passes its native versionName the same way. */
    ...(isCapacitorNative() ? { version: CAPACITOR_APP_VERSION } : {}),
  });

  /* Every @client entry the cache must always be able to fulfil, with its
     default. Shared by the unconditional reset below (first visit, logout)
     and the fill-if-absent pass after a persisted-cache restore. */
  const localCacheDefaults = [
    { query: GET_AUTH, data: resolverAuth.defaults },
    { query: GET_GIFT_FLOW_INFO, data: resolverGiftFlow.defaults },
    { query: GET_REFERRAL_DISCOUNT, data: resolverReferralDiscount.defaults },
    { query: GET_GUEST_FREE_BOX_CAMPAIGN_DISCOUNT, data: resolverGuestFreeBoxCampaignDiscount.defaults },
    { query: GET_GUEST_PROMO_CODE_DISCOUNT, data: resolverGuestFreeBoxCampaignDiscount.defaults },
    { query: GET_LOCAL_SHOPPING_CART, data: resolverLocalCart.defaults },
  ];

  /**
   * Populate default cache values for non-logged in member
   * */
  const resetCacheToDefault = () => {
    localCacheDefaults.forEach(({ query, data }) => {
      cache.writeQuery({ query, data });
    });
  };

  /* Restored blobs can predate a local-state field (old visitor returning
     after a deploy that added one, or a hand-seeded test blob) — and defaults
     are otherwise only written when NO blob exists. Run after restore to make
     "the cache can always fulfil every @client default" an enforced invariant
     rather than an accident of persistence history. The cart is migrated
     first: unlike the other entries, resetting it destroys a guest's saved
     items, so old-shape carts get padded up to the current selection set
     before the fill-if-absent pass judges what it can fulfil. */
  const ensureLocalCacheDefaults = async () => {
    // Pad restored local cart entries that predate newer fields BEFORE the defaults pass: an unreadable
    // (old-shape) cart would otherwise be replaced with the empty default.
    migratePersistedLocalCart(cache);
    writeMissingCacheDefaults(cache, localCacheDefaults);
    if (!isLoggedIn()) {
      await refreshLocalCartOrderMinimumFlags(cache, client);
    }
  };

  // Creates default cache when user lands in the app for the first time
  if (!localStorage.getItem(import.meta.env.REACT_APP_STORE_LOCAL_STORAGE)) {
    resetCacheToDefault();
  }

  // Cleans cache with default data once user logs out and logs back in
  client.onResetStore(() => {
    resetCacheToDefault();
  });

  return { client, persistent, cache, ensureLocalCacheDefaults };
};

const { client, persistent, ensureLocalCacheDefaults } = generateApolloClient();

export { persistent, client, ensureLocalCacheDefaults };
